import React, { useState } from 'react';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, Link } from "react-router-dom";
import {
    Paper,
    Box,
    Button,
    TextField,
    Typography,
    Container,
    IconButton,
    InputAdornment,
    CircularProgress,
} from '@mui/material';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import PasswordIcon from '@mui/icons-material/Password';
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { Helmet } from 'react-helmet-async';

import FormError from "../components/Forms/FormError";
import { loginUser } from "../api/auth";
import { useAuth } from "../context/AuthContext";

// Validation schema moved outside component (static)
const createLoginSchema = (t) => yup.object().shape({
    email: yup
        .string()
        .trim()
        .lowercase()
        .email(t("errors.email_invalid"))
        .required(t("errors.email_required")),
    password: yup.string().required(t("errors.password_required")).min(6, t("errors.password_min")),
});

const Login = () => {
    // Hooks
    const theme = useTheme();
    const { t } = useTranslation();
    const { login } = useAuth();
    const navigate = useNavigate();

    // State
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const loginSchema = React.useMemo(() => createLoginSchema(t), [t]);

    // Handlers
    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (e) => {
        e.preventDefault(); // Prevent input losing focus
    };

    // React Hook Form setup
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(loginSchema),
    });

    // Form submission
    const onSubmit = async (data) => {
        setLoading(true);
        setErrorMessage(""); // Clear previous errors on submit

        try {
            const user = await loginUser(data.email, data.password);
            if (user) {
                login();

                if (user.welcomeMessageShown == true) {
                    navigate("/izsoles");
                } else {
                    navigate("/sveicinats");
                }
            } else {
                setErrorMessage(t("errors.invalid_credentials"));
            }
        } catch (error) {
            setErrorMessage(t("errors.err"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>{t("seo.title.login")}</title>
                <meta name="description" content={t('seo.description.login')} />
                <meta property="og:title" content={t('seo.title.login')} />
                <meta property="og:description" content={t('seo.description.login')} />
            </Helmet>
            <Container
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: { sm: '80vh' },
                    py: {
                        xs: 0,
                        md: 2
                    },
                    b: {
                        xs: 6
                    },
                    px: {
                        xs: 0,
                        md: 2
                    }
                }}
            >
                <Paper
                    sx={{
                        width: '100%',
                        maxWidth: '400px',
                        p: 3,
                        borderRadius: {
                            xs: 0,
                            md: 4
                        },
                        pt: {
                            xs: 6
                        },
                        py: {
                            xs: 7,
                            md: 3
                        },
                        border: { xs: "none", sm: "1px solid rgb(226, 234, 243)" },
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography variant="h2" sx={{ fontWeight: "bold", mb: 2, textAlign: 'left', color: "primary.main" }}>
                        {t("login_page.title")}
                    </Typography>

                    {errorMessage && (
                        <FormError errorMessage={errorMessage} />
                    )}

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            label={t("forms.email")}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            size="normal"
                            {...register('email')}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                        />

                        <TextField
                            label={t("forms.password")}
                            type={showPassword ? 'text' : 'password'}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            {...register('password')}
                            error={!!errors.password}
                            helperText={errors.password?.message}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            disableElevation
                            fullWidth
                            color='secondary'
                            size="large"
                            sx={{
                                mt: 2, mb: 1,
                                // STRIPE SHADOW
                                // boxShadow: '0px 0px 0px rgba(0,0,0,0.0)',
                                '&:hover': {
                                    backgroundColor: 'secondary.light',
                                    // boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.05)',
                                }
                            }}
                            disabled={loading}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                t("login_page.submit")
                            )}
                        </Button>
                    </form>

                    <Box sx={{ textAlign: 'center', mt: 0 }}>
                        <Button
                            color="primary"
                            component={Link}
                            fullWidth
                            size="large"
                            to="/registreties"
                            sx={{ fontWeight: 'bold' }}
                        >
                            {t("login_page.create_account")}
                        </Button>
                    </Box>
                </Paper>

                <Box sx={{ textAlign: 'center', mt: { xs: 0, sm: 1 }, py: { xs: 2, sm: 0 } }}>
                    <Button
                        color="primary"
                        component={Link}
                        to="/aizmirsuparoli"
                        sx={{ fontStyle: "italic" }}
                        size="large"
                    >
                        {t("login_page.forgot_password")}
                    </Button>
                </Box>
            </Container >
        </>
    );
};

export default Login;
